// src/App.tsx

import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { StatsigProvider, useClientAsyncInit } from "@statsig/react-bindings";
import { lazy, Suspense, useState } from "react";
import { useMessageLaunch } from "./logic/hooks/useMessageLaunch";
import Layout from "./components/app/Layout";
import { AppToaster } from "./components/app/AppToaster/AppToaster";
import { StatsigRuntime } from "./components/app/StatsigRuntime/StatsigRuntime";
import { CareerAccessGate } from "./components/app/CareerAccessGate/CareerAccessGate";
import MainPage from "./components/pages/MainPage/MainPage";
import { WheelCampaignFallback } from "./components/pages/WheelCampaignPage/components/WheelCampaignFallback/WheelCampaignFallback";
import { readCachedAuthIdentity } from "./logic/helpers/authIdentityStorage";
import { useInputModality } from "./logic/hooks/useInputModality";
import "./components/app/styles/globals.scss";

const GnomeCareerPage = lazy(
    () => import("./components/pages/GnomeCareerPage/GnomeCareerPage"),
);
const HolyWheelPage = lazy(
    () => import("./components/pages/WheelCampaignPage/WheelCampaignPage"),
);
const GnomeCareerPathPage = lazy(
    () => import("./components/pages/GnomeCareerPathPage/GnomeCareerPathPage"),
);
const GnomeChangingRoomPage = lazy(
    () =>
        import("./components/pages/GnomeChangingRoomPage/GnomeChangingRoomPage"),
);
const HapticLabPage = import.meta.env.DEV
    ? lazy(() => import("./components/pages/HapticLabPage/HapticLabPage"))
    : null;

function GnomeChangingRoomRedirect() {
    const { search } = useLocation();

    return (
        <Navigate
            to={{ pathname: "/gnome/career/changing-room", search }}
            replace
        />
    );
}

const STATSIG_CLIENT_KEY = "client-8z0vz4Oq9pECsnbpGYYojyBAngz4oPz0CCUxvcDQVXT";

export default function App() {
    useInputModality();
    useMessageLaunch();
    // ___initialize Statsig with the last server-provided PB identity.
    // First-time users start anonymously and are updated after authentication.
    const [initialStatsigUserId] = useState(
        () => readCachedAuthIdentity()?.userId,
    );
    // Keep this in app memory: route changes preserve it, while a genuine
    // Mini App reload starts a fresh first-entry presentation.
    const [isInitialCareerEntryPending, setIsInitialCareerEntryPending] =
        useState(true);
    const { client } = useClientAsyncInit(STATSIG_CLIENT_KEY, {
        userID: initialStatsigUserId,
    });

    return (
        <StatsigProvider client={client}>
            <StatsigRuntime />
            <Routes>
                <Route element={<Layout />}>
                    <Route path="/" element={<MainPage />} />
                    <Route path="/inbox" element={<MainPage />} />
                    <Route
                        path="/wheels/:wheelCode"
                        element={
                            <Suspense fallback={<WheelCampaignFallback />}>
                                <HolyWheelPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/holy-wheel"
                        element={
                            <Suspense fallback={<WheelCampaignFallback />}>
                                <HolyWheelPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/gnome/career"
                        element={
                            <CareerAccessGate>
                                <Suspense fallback={null}>
                                    <GnomeCareerPage
                                        shouldRevealInitialEntry={
                                            isInitialCareerEntryPending
                                        }
                                        onInitialEntryRevealStarted={() =>
                                            setIsInitialCareerEntryPending(
                                                false,
                                            )
                                        }
                                    />
                                </Suspense>
                            </CareerAccessGate>
                        }
                    >
                        <Route
                            path="path"
                            element={
                                <Suspense fallback={null}>
                                    <GnomeCareerPathPage />
                                </Suspense>
                            }
                        />
                        <Route
                            path="changing-room"
                            element={
                                <Suspense fallback={null}>
                                    <GnomeChangingRoomPage />
                                </Suspense>
                            }
                        />
                    </Route>
                    <Route
                        path="/gnome/changing-room"
                        element={<GnomeChangingRoomRedirect />}
                    />
                    <Route
                        path="/gnome/customize"
                        element={<GnomeChangingRoomRedirect />}
                    />
                    {HapticLabPage !== null && (
                        <Route
                            path="/dev/haptics"
                            element={
                                <Suspense fallback={null}>
                                    <HapticLabPage />
                                </Suspense>
                            }
                        />
                    )}
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <AppToaster />
        </StatsigProvider>
    );
}
